/* ==========================================================================
   Oddit Announcement Bar
   --------------------------------------------------------------------------
   - Mobile: CSS marquee. JS clones the inner's children once so the
     translateX(-50%) animation loops seamlessly.
   - Desktop static_all: shows everything inline (no JS needed)
   - Desktop fade / marquee modes also handled when set via data attribute
   --------------------------------------------------------------------------
   Uses requestAnimationFrame timestamps (NOT setInterval) so the rotation
   pauses automatically when the tab is backgrounded.
   ========================================================================== */

(function () {
  var SECTION_SELECTOR = '.oddit-announcement-bar';
  var MOBILE_BREAKPOINT = '(max-width: 749px)';

  var sectionState = new Map();

  function initSection(section) {
    if (!section || sectionState.has(section)) return;

    var slides = Array.prototype.slice.call(
      section.querySelectorAll('[data-announcement-slide]')
    );
    if (slides.length < 2) return;

    var intervalSeconds = parseFloat(section.dataset.rotationInterval) || 5;
    var intervalMs = intervalSeconds * 1000;

    var mobileQuery = window.matchMedia(MOBILE_BREAKPOINT);
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    var state = {
      slides: slides,
      activeIndex: 0,
      rafId: null,
      lastAdvance: 0,
      mobileQuery: mobileQuery,
      reducedMotion: reducedMotion,
      handlers: {},
    };

    function setActive(nextIndex) {
      var current = state.slides[state.activeIndex];
      var next = state.slides[nextIndex];
      if (current) current.classList.remove('is-active');
      if (next) next.classList.add('is-active');
      state.activeIndex = nextIndex;
    }

    function advance() {
      var nextIndex = (state.activeIndex + 1) % state.slides.length;
      setActive(nextIndex);
    }

    function tick(now) {
      if (now - state.lastAdvance >= intervalMs) {
        advance();
        state.lastAdvance = now;
      }
      state.rafId = requestAnimationFrame(tick);
    }

    function start() {
      if (state.rafId !== null) return;
      state.lastAdvance = performance.now();
      state.rafId = requestAnimationFrame(tick);
    }

    function stop() {
      if (state.rafId !== null) {
        cancelAnimationFrame(state.rafId);
        state.rafId = null;
      }
    }

    function shouldRotate() {
      if (reducedMotion.matches) return false;
      var mode = section.dataset.rotationMode || 'static_all';

      // Mobile uses CSS marquee, not JS rotation.
      if (mobileQuery.matches) return false;

      // On desktop, only rotate when explicitly fade/marquee mode.
      if (mode === 'fade') return true;
      return false;
    }

    function setupMobileMarquee() {
      var inner = section.querySelector('.oddit-announcement-bar__inner');
      if (!inner || inner.dataset.marqueeCloned === 'true') return;
      var originals = Array.prototype.slice.call(inner.children);
      originals.forEach(function (child) {
        var clone = child.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-marquee-clone', '');
        if (clone.hasAttribute('data-announcement-slide')) {
          clone.removeAttribute('data-announcement-slide');
        }
        clone.classList.remove('is-active');
        inner.appendChild(clone);
      });
      inner.dataset.marqueeCloned = 'true';
    }

    function teardownMobileMarquee() {
      var inner = section.querySelector('.oddit-announcement-bar__inner');
      if (!inner || inner.dataset.marqueeCloned !== 'true') return;
      var clones = inner.querySelectorAll('[data-marquee-clone]');
      clones.forEach(function (node) {
        node.parentNode.removeChild(node);
      });
      inner.dataset.marqueeCloned = 'false';
    }

    function refresh() {
      if (mobileQuery.matches) {
        stop();
        setActive(0);
        setupMobileMarquee();
        return;
      }

      teardownMobileMarquee();

      if (shouldRotate()) {
        start();
      } else {
        stop();
        // Reset to first slide so static_all renders predictably on desktop.
        if ((section.dataset.rotationMode || 'static_all') === 'static_all') {
          setActive(0);
        }
      }
    }

    state.handlers.visibility = function () {
      if (!shouldRotate()) return;
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    state.handlers.media = function () {
      refresh();
    };

    state.handlers.reduced = function () {
      refresh();
    };

    document.addEventListener('visibilitychange', state.handlers.visibility);

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', state.handlers.media);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(state.handlers.media);
    }

    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', state.handlers.reduced);
    } else if (typeof reducedMotion.addListener === 'function') {
      reducedMotion.addListener(state.handlers.reduced);
    }

    sectionState.set(section, state);
    refresh();
  }

  function destroySection(section) {
    var state = sectionState.get(section);
    if (!state) return;

    if (state.rafId !== null) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }

    document.removeEventListener('visibilitychange', state.handlers.visibility);

    if (state.mobileQuery) {
      if (typeof state.mobileQuery.removeEventListener === 'function') {
        state.mobileQuery.removeEventListener('change', state.handlers.media);
      } else if (typeof state.mobileQuery.removeListener === 'function') {
        state.mobileQuery.removeListener(state.handlers.media);
      }
    }

    if (state.reducedMotion) {
      if (typeof state.reducedMotion.removeEventListener === 'function') {
        state.reducedMotion.removeEventListener('change', state.handlers.reduced);
      } else if (typeof state.reducedMotion.removeListener === 'function') {
        state.reducedMotion.removeListener(state.handlers.reduced);
      }
    }

    sectionState.delete(section);
  }

  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();